/**
 * Types and plain wording for the investigation Work section (#672).
 * Kept apart from the component so fast refresh works and the wording is unit-testable.
 */

// ── Types (backend: work_formation.work_for, episode_presentation.present) ──

export type DataLabel = 'demo' | 'real';
export type RelevanceBasis = 'affects_active_work' | 'helps_open_question' | 'unexpected_connection' | 'explicit_user_request';

export interface EpisodeRecord {
  cluster?: { title: string; data_label?: DataLabel };
  shortlist?: Array<{ capability: string; description?: string; reason: string }>;
  ineligible?: Array<{ capability: string; reason: string }>;
  judgement?: { ok: boolean; reportable: number | null; choice: string | null; model?: string } | null;
  selection?: {
    by: 'jev' | 'declared_order';
    confidence: number | null;
    probabilities?: Record<string, number>;
    provenance?: { question_set?: string | null; schema_version?: string };
  } | null;
  chosen?: string | null;
  fetch?: { rule: string; returned?: number; error?: string } | null;
  independence?: { upstream: string } | null;
  result?: string | null;
}

export interface Presentation {
  summary: string;
  details: {
    evidence: Array<{ ref: string; title?: string; source: string; when?: string; distance_km?: number }>;
    rule: { id: string; version: string; text: string } | null;
    limitations: {
      establishes: string;
      does_not_establish: string;
      dependencies: string | null;
      other_sources: string[];
    };
  };
}

export interface WorkItem {
  id: string;
  created_at?: string;
  data_label: DataLabel;
  why_it_matters: string;
  question: string;
  required_evidence: string;
  useful_result: string;
  relevance: { basis: RelevanceBasis; grounds: string; link: string | null; status: 'stated' | 'possible' };
  episode_id: string | null;
  result: string | null;
  episode: EpisodeRecord | null;
  presentation: Presentation | null;
}

export interface WorkResponse {
  investigation_id: string;
  work?: WorkItem[];
  check_enabled?: boolean;
}

export interface CheckForm {
  why_it_matters: string;
  question: string;
  required_evidence: string;
  useful_result: string;
  relevance_basis: RelevanceBasis;
  relevance_link: string;
}

// ── Wording ──────────────────────────────────────────────────────────────────

export const BASIS_OPTIONS: Array<[RelevanceBasis, string]> = [
  ['affects_active_work', 'Affects active work'],
  ['helps_open_question', 'Helps an open question'],
  ['unexpected_connection', 'Unexpected connection'],
  ['explicit_user_request', 'Explicit user request (linked)'],
];

/** Relevance line: an intersection or forecast is only a *possible* relevance, never "affects". */
export function relevanceLine(relevance: WorkItem['relevance']): string {
  const possible = relevance.status !== 'stated';
  const basis: Record<RelevanceBasis, string> = {
    affects_active_work: possible ? 'May affect active work' : 'Affects active work',
    helps_open_question: 'Helps an open question',
    unexpected_connection: 'Unexpected connection',
    explicit_user_request: 'Explicit user request',
  };
  const grounds: Record<string, string> = { intersection: 'overlap only', forecast: 'forecast only' };
  const status = possible ? `possible (${grounds[relevance.grounds] ?? `${relevance.grounds} only`})` : 'stated';
  return `${basis[relevance.basis] ?? relevance.basis} · ${status}`;
}

/** Plain explanation of how the source was selected (no probabilities). */
export function selectionExplanation(record: EpisodeRecord | null): string {
  if (!record) return 'This requirement has not been checked.';
  const available = record.shortlist?.length ?? 0;
  if (record.result === 'gap') return 'No source was eligible, so nothing was checked.';
  if (record.selection?.by === 'declared_order' && record.chosen) {
    return `${record.chosen} was used because it is first in the declared order; Jev's choice was not available.`;
  }
  if (record.result === 'abstained_none') {
    return `Jev chose none of the ${available} available sources as likely to hold an independent report, so nothing was checked.`;
  }
  if (record.result === 'abstained_not_reportable') {
    return 'Jev judged the report not a discrete, reportable event, so nothing was checked.';
  }
  if (record.chosen) {
    return `Jev chose ${record.chosen} from ${available} available source${available === 1 ? '' : 's'}, using each source's description and check history.`;
  }
  return 'No source was chosen.';
}
